import { MarketingAttributionMultiTouchModel } from '@server/marketing-attribution';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { colors, Tag, Typography } from '@sweep-io/sweep-design';
import { translateWeightName } from '../../pages/canvas-pages/canvas-hubspot-page/marketing-attribution/utils';
import { IntegerPicker } from '../../pages/assignments/group-members/IntegerPicker';
import { Percentage } from '../../common/Percentage';
import { DataTable } from '../../common/table/DataTable';
import { DataTableId } from '../../common/table/utils';

const columns = [
  {
    headerName: 'Name',
    field: 'name',
    width: '300px',
  },
  {
    field: 'weight',
    headerName: 'Weight distribution',
  },
];

const headerText = (modelType: string) => {
  switch (modelType) {
    case 'EVEN_DISTRIBUTION':
      return 'Even Weights';
    case 'MULTI_TOUCH':
      return 'Custom Weights';
    default:
      return 'Fixed weights distribution';
  }
};

const subHeaderText = (modelType: string) => {
  switch (modelType) {
    case 'EVEN_DISTRIBUTION':
      return 'In even distribution, all weight will be distributed evenly between campaigns.';
    case 'MULTI_TOUCH':
      return 'Distribute revenue across touchpoints';
    default:
      return 'Attribution is automatically assigned based on this model';
  }
};

interface WeightsProps {
  modelType: string;
  weights?: MarketingAttributionMultiTouchModel['weights'];
  readonly?: boolean;
  onChange?: (weights: MarketingAttributionMultiTouchModel['weights']) => void;
}

export const Weights = ({ modelType, weights, readonly, onChange }: WeightsProps) => {
  const totalWeights = Object.values(weights ?? {}).reduce((acc, curr) => acc + curr, 0);
  const showExampleBadge = modelType === 'EVEN_DISTRIBUTION';

  const rows = Object.entries(weights ?? {}).map(([key, value]) => ({
    id: key,
    name: translateWeightName(key),
    weight: (
      <Box display="flex" alignItems="center" gap={2} width="100%">
        <Box className="show-on-hover">
          <IntegerPicker
            useImmediateUpdate
            disabled={readonly}
            value={value * 100}
            onChange={(weight) => {
              onChange?.({ ...weights, [key]: weight ? weight / 100 : 0 } as any);
            }}
            min={1}
          />
        </Box>

        <Box width="120px">
          <Percentage numerator={value} active disabled={readonly} />
        </Box>
      </Box>
    ),
    disabled: readonly,
  }));

  return (
    <Stack
      sx={{
        pt: 2,
        gap: 1,
        '& .show-on-hover': {
          button: { opacity: 0 },
          '.MuiOutlinedInput-root fieldset': { borderColor: 'transparent' },
          '.MuiDivider-root': { opacity: 0 },
        },
        '&:hover .hide-on-hover': {
          display: 'none',
        },
      }}
    >
      <Typography variant="h4">{headerText(modelType)}</Typography>
      <Typography variant="body" color={colors.grey[900]}>
        {subHeaderText(modelType)}
      </Typography>

      <Stack
        height="100%"
        position="relative"
        sx={{
          width: '670px',
        }}
        gap={2}
      >
        {showExampleBadge && (
          <Box sx={{ position: 'absolute', top: '-10px', right: '20px', zIndex: 1 }}>
            <Tag color={colors.night[900]} size="medium" label="Example" />
          </Box>
        )}
        <DataTable
          tableId={DataTableId.MarketingAttributionWeightsTable}
          columns={columns}
          rows={rows}
        />
      </Stack>

      {(totalWeights > 1 || totalWeights < 1) && (
        <Box sx={{ left: '345px', position: 'relative', width: '310px' }}>
          <Typography variant="body-medium" color={colors.blush[500]}>
            Fix weight distribution to a total of 100 percent
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
